import { Button } from '@bambi/component-library/src/components/Button/Button';
import { MapIcon } from '@heroicons/react/24/outline';
import { first, last } from 'lodash-es';

import { PassengerRead } from 'src/common/external/bambi-api/bambiApi';
import {
  RouteMultiloadTemplateRead,
  TripLocationDetailRead,
} from 'src/common/external/bambi-api/routesApi';
import { EmptyState } from 'src/common/primitives/EmptyState/EmptyState';
import { ModalFooter } from 'src/common/primitives/Modal/ModalFooter';

import {
  RouteMultiloadTemplateFormValues,
  RouteTripTemplateFormValues,
} from '../types';
import { useTripTemplatesList } from '../useTripTemplatesList';
import { RouteTripTemplateCard } from './RouteTripTemplateCard';

interface RouteTripTemplateListProps {
  tripTemplates: Array<RouteTripTemplateFormValues & { __id: string }>;
  multiloadTripTemplates: RouteMultiloadTemplateRead[];
  workflowMode: 'single' | 'bulk';
  canCreateMore: boolean;
  onAddTripTemplate: () => void;
  onEditTripTemplate: (tripTemplate: RouteTripTemplateFormValues) => void;
  onDeleteTripTemplate: (
    tripTemplate: RouteTripTemplateFormValues & { __id: string }
  ) => void;
  onEditMultiLoadTripTemplate: (
    tripTemplate: RouteMultiloadTemplateFormValues
  ) => void;
  onRestoreMultiLoadTripTemplate: (
    tripTemplate: RouteMultiloadTemplateFormValues
  ) => void;
  onCancel: () => void;
  onSaveAndClose: () => void;
  onSaveAndNext: () => void;
  onMultiLoadTrips: () => void;
}
export function RouteTripTemplateList({
  tripTemplates,
  multiloadTripTemplates,
  workflowMode,
  canCreateMore,
  onAddTripTemplate,
  onEditTripTemplate,
  onDeleteTripTemplate,
  onCancel,
  onSaveAndClose,
  onSaveAndNext,
  onMultiLoadTrips,
  onEditMultiLoadTripTemplate,
  onRestoreMultiLoadTripTemplate,
}: RouteTripTemplateListProps) {
  const { renderableTripTemplates } = useTripTemplatesList(
    tripTemplates,
    multiloadTripTemplates
  );

  if (!renderableTripTemplates.length) {
    return (
      <EmptyState
        entityName="Matcher"
        Icon={<MapIcon className="w-8" />}
        description=""
        Action={
          <Button variant="primary" onClick={onAddTripTemplate}>
            Add a new Matcher
          </Button>
        }
      />
    );
  }
  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between">
          <p className="text-sm text-gray-500">
            Manage the list of trip matchers for this template.
          </p>
          <Button
            variant="primary-outline"
            className="whitespace-nowrap"
            onClick={onAddTripTemplate}
          >
            Add a new Matcher
          </Button>
        </div>
        <div className="flex max-h-[45vh] flex-col gap-2 overflow-auto">
          {renderableTripTemplates.map((field) => {
            if (field.type === 'multi-load') {
              // Gather any preset accompanying passengers from the trip templates
              const numAccompanyingPassengers = field.trip_templates.reduce(
                (c, template) => {
                  if (template && template.trip.num_accompanying_passengers) {
                    return c + template.trip.num_accompanying_passengers;
                  }
                  return c;
                },
                0
              );
              // Each multi-load template has 2 trip templates (pickup and
              // dropoff), so we need to divide by 2 to get the number of
              // passengers and we're showing one of them in the card, so minus
              // 1.
              const countPassengers =
                field.trip_templates.length / 2 - 1 + numAccompanyingPassengers;
              const firstTrip = first(field.trip_templates)?.trip;
              const lastTrip = last(field.trip_templates)?.trip;

              // Casting because it can't be undefined
              const passengerToShow = firstTrip?.passenger as PassengerRead;
              const pickup = firstTrip?.pickup as TripLocationDetailRead;
              const dropoff = lastTrip?.dropoff as TripLocationDetailRead;

              return (
                <RouteTripTemplateCard
                  key={field.__id}
                  tripTemplate={{
                    passenger_id: passengerToShow.id,
                    passenger: passengerToShow,
                    pickup,
                    dropoff,
                    __id: field.__id,
                    scheduled_pickup_time: firstTrip?.scheduled_pickup_time!,
                    route_template_id: firstTrip?.route_template_id!,
                    space_type: firstTrip?.space_type!,
                    num_accompanying_passengers: countPassengers,
                  }}
                  variant="multi-load"
                  handleEditTripMatcher={() =>
                    onEditMultiLoadTripTemplate(
                      // Field is close enough for the form, we're just missing DB only fields like created_at, etc.
                      field as unknown as RouteMultiloadTemplateFormValues
                    )
                  }
                  handleDeleteTripMatcher={() =>
                    onRestoreMultiLoadTripTemplate(
                      field as unknown as RouteMultiloadTemplateFormValues
                    )
                  }
                />
              );
            }
            return (
              <RouteTripTemplateCard
                key={field.__id}
                tripTemplate={field}
                handleEditTripMatcher={onEditTripTemplate}
                handleDeleteTripMatcher={() => onDeleteTripTemplate(field)}
              />
            );
          })}
        </div>
      </div>
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <div className="flex flex-row gap-2">
          <Button
            variant="primary-outline"
            onClick={onMultiLoadTrips}
            disabled={renderableTripTemplates.length === 1}
          >
            Multi-load trips
          </Button>

          {workflowMode === 'single' || !canCreateMore ? (
            <Button variant="primary" onClick={onSaveAndClose}>
              Save
            </Button>
          ) : (
            <div className="flex flex-row gap-2">
              <Button variant="primary-outline" onClick={onSaveAndClose}>
                Save and Close
              </Button>
              <Button variant="primary" onClick={onSaveAndNext}>
                Save and Create more
              </Button>
            </div>
          )}
        </div>
      </ModalFooter>
    </>
  );
}
